import {SLUG_ERROR_MESSAGE} from '@github-ui/copilot-chat/utils/custom-copilots-helpers'
import {Dialog, FormControl, Stack, TextInput} from '@primer/react'
import {useCallback, useRef, useState} from 'react'
import type {FileReference} from '@github-ui/copilot-chat/utils/copilot-chat-types'

import {SpaceDialogFooter} from './SpaceDialogFooter'
import {SpacesOwnerDropdown, type OwnerItem} from './SpacesOwnerDropdown'
import {useUpsertCopilotSpace} from '../hooks/use-upsert-copilot-space-no-chat-state'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {CopilotSpaceResource} from '../types'

export function SpacesSelectionCreateSpaceDialog({
  closeDialog,
  currentFile,
  returnFocusRef,
}: {
  closeDialog: () => void
  currentFile?: FileReference
  returnFocusRef?: React.RefObject<HTMLButtonElement | null>
}) {
  const nameInputRef = useRef<HTMLInputElement>(null)
  const [name, setName] = useState('')
  const [errorMessages, setErrorMessages] = useState<Record<string, string>>({})
  const {upsertCopilotSpace, isPending: isSaving} = useUpsertCopilotSpace()
  const [owner, setOwner] = useState<OwnerItem | null>(null)
  const MAX_NAME_LENGTH = 255

  const dropdownOnSelect = useCallback((newOwner: OwnerItem) => setOwner(newOwner), [])

  const clearErrors = useCallback(
    (fields: string[]) => {
      const updatedErrorMessages = {...errorMessages}
      for (const field of fields) {
        if (updatedErrorMessages[field]) {
          delete updatedErrorMessages[field]
        }
      }
      setErrorMessages(updatedErrorMessages)
    },
    [errorMessages],
  )

  const saveSpace = async (): Promise<void> => {
    if (isSaving) return

    try {
      const resources: CopilotSpaceResource[] = []

      // currentFile is set when creating a new space from the "Add to space" button on a file
      if (currentFile) {
        resources.push({
          id: '',
          repositoryId: currentFile.repoID,
          nwo: `${currentFile.repoOwner}/${currentFile.repoName}`,
          filePath: currentFile.path,
          sha: currentFile.commitOID,
          fileExists: true,
          type: 'github_file' as const,
          markedForDestroy: false,
        })
      }

      const spaceData = {
        name,
        owner_id: owner?.id,
        owner_type: owner?.type,
        resources,
      }

      await upsertCopilotSpace(spaceData)
      setErrorMessages({})
      closeDialog()
      sendEvent('copilot_spaces.code_view', {
        target: 'COPILOT_SPACES_CODE_VIEW_MENU_SPACE_CREATED',
      })
    } catch (errors) {
      const errs = errors ?? {}
      setErrorMessages(errs as Record<string, string>)
    }
  }
  const handleCancel = () => {
    closeDialog()
  }

  return (
    <Dialog
      position={{narrow: 'fullscreen'}}
      title="Create a new Space"
      onClose={closeDialog}
      width="large"
      aria-label="Create a new Space"
      returnFocusRef={returnFocusRef}
      renderFooter={() => (
        <SpaceDialogFooter onCancel={handleCancel} onSave={saveSpace} isLoading={isSaving} saveButtonText="Create" />
      )}
    >
      <form
        onSubmit={async e => {
          e.preventDefault()
          e.stopPropagation()
          await saveSpace()
        }}
      >
        <Stack>
          <FormControl>
            <FormControl.Label required>Name</FormControl.Label>
            <TextInput
              ref={nameInputRef}
              autoFocus
              block
              data-testid="copilot-space-name-input"
              name="copilot-space-name"
              maxLength={MAX_NAME_LENGTH}
              value={name}
              onChange={e => {
                setName(e.target.value)
                clearErrors(['name', 'slug'])
              }}
            />
            <FormControl.Caption>
              {name.length}/{MAX_NAME_LENGTH}
            </FormControl.Caption>
            {errorMessages.name ? (
              <FormControl.Validation variant="error">
                <span>Name {errorMessages.name}</span>
              </FormControl.Validation>
            ) : errorMessages.slug ? (
              <FormControl.Validation variant="error">
                <span>{SLUG_ERROR_MESSAGE}</span>
              </FormControl.Validation>
            ) : null}
          </FormControl>
          <SpacesOwnerDropdown onSelect={dropdownOnSelect} repoId={currentFile?.repoID} />
        </Stack>
      </form>
    </Dialog>
  )
}
